/*
 * Side cart behaviour for sections/cart-drawer-pj.liquid. Loaded once (deferred, cached) from layout/theme.liquid.
 * - every form posting to /cart/add is sent with fetch to /cart/add.js together with the drawer section
 *   (Section Rendering API), only [data-pj-cart-inner] is swapped in, and the drawer opens;
 * - quantity +/-, remove, "Add free gift" and the "You may also like" Add buttons use /cart/change.js and
 *   /cart/add.js the same way;
 * - [data-pj-cart-open] links (header / bottom bar) open the drawer instead of going to /cart;
 * - [data-pj-cart-count] badges and [data-pj-cart-label] links follow the cart count;
 * - cart changes made by Horizon's own code (the /cart page, apps using Shopify.actions) are picked up from
 *   Shopify's standard 'shopify:cart:lines-update' event.
 * On /cart the section renders an empty shell with data-pj-cart-page: nothing is intercepted there.
 */
(() => {
  if (window.pjCart) return;
  const SECTION = 'cart-drawer-pj';
  const $root = () => document.querySelector('[data-pj-cart]');
  const $panel = () => $root()?.querySelector('[data-pj-cart-panel]');
  const isOpen = () => !!$root()?.classList.contains('is-open');
  const onCartPage = () => !!$root()?.hasAttribute('data-pj-cart-page');
  const route = (key, fallback) => $root()?.dataset[key] || fallback;
  let opener = null;
  let busy = 0;

  const setBusy = (on) => {
    busy = Math.max(0, busy + (on ? 1 : -1));
    $root()?.toggleAttribute('data-busy', busy > 0);
  };

  const setCounts = (n) => {
    const count = Number(n) || 0;
    document.querySelectorAll('[data-pj-cart-count]').forEach((el) => {
      el.textContent = count;
      if (el.hasAttribute('data-pj-cart-count-hide-empty')) el.hidden = count === 0;
    });
    document.querySelectorAll('[data-pj-cart-label]').forEach((el) => {
      el.setAttribute('aria-label', `Cart, ${count} ${count === 1 ? 'item' : 'items'}`);
    });
  };

  // polite screen-reader message (the status element sits outside the swapped part, so it survives re-renders)
  const announce = (text) => {
    const status = $root()?.querySelector('[data-pj-cart-status]');
    if (!status) return;
    status.textContent = '';
    requestAnimationFrame(() => {
      status.textContent = text;
    });
  };

  const showError = (message) => {
    const slot = $root()?.querySelector('[data-pj-cart-error]');
    if (!slot) return;
    slot.textContent = message || '';
    slot.hidden = !message;
  };

  // product images wait in data-pj-src until the drawer is shown, so a closed drawer downloads nothing
  const loadImages = (scope) => {
    scope?.querySelectorAll('img[data-pj-src]').forEach((img) => {
      img.src = img.getAttribute('data-pj-src');
      img.removeAttribute('data-pj-src');
    });
  };

  // fill the progress bar from `from`% to its rendered width; the real width is kept in data-target, so a second
  // call before the next frame (render() then open()) cannot leave the bar at a temporary value
  const fillBar = (fill, from) => {
    if (!fill) return;
    const target = fill.dataset.target || (fill.dataset.target = fill.style.width);
    fill.style.width = from + '%';
    fill.getBoundingClientRect();
    requestAnimationFrame(() => {
      fill.style.width = target;
    });
  };

  const render = (html) => {
    const root = $root();
    if (!root || !html) return false;
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-pj-cart-inner]');
    const current = root.querySelector('[data-pj-cart-inner]');
    if (!next || !current) return false;
    const oldFill = current.querySelector('[data-pj-cart-fill]');
    const oldPct = oldFill ? parseFloat(oldFill.dataset.target || oldFill.style.width) || 0 : 0;
    const active = document.activeElement;
    const hadFocus = current.contains(active);
    const focusKey = hadFocus ? active.getAttribute('data-focus-key') : null;
    const scrollTop = current.querySelector('[data-pj-cart-scroll]')?.scrollTop || 0;
    if (isOpen()) loadImages(next);
    current.replaceWith(next);
    const scroller = next.querySelector('[data-pj-cart-scroll]');
    if (scroller) scroller.scrollTop = scrollTop;
    fillBar(next.querySelector('[data-pj-cart-fill]'), oldPct);
    setCounts(next.getAttribute('data-count'));
    if (hadFocus) {
      const again = focusKey ? next.querySelector('[data-focus-key="' + focusKey + '"]') : null;
      (again || $panel())?.focus({ preventScroll: true });
    }
    return true;
  };

  const request = async (url, options = {}) => {
    // a hung request would keep the cart busy (and locked) forever, so give up after 20 s
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    let response;
    try {
      response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...(options.headers || {}) },
      });
    } catch (error) {
      throw new Error(error.name === 'AbortError' ? 'The network is slow. Please try again.' : 'Could not reach the store. Please check your connection.');
    } finally {
      clearTimeout(timer);
    }
    let data = null;
    try {
      data = await response.json();
    } catch (_) {}
    if (!response.ok) {
      const error = new Error((data && (data.description || data.message)) || 'Something went wrong. Please try again.');
      error.status = response.status;
      throw error;
    }
    return data;
  };

  const refresh = async () => {
    const data = await request(route('rootUrl', '/') + '?sections=' + SECTION);
    render(data && data[SECTION]);
  };

  const open = (animate = true, from = null) => {
    const root = $root();
    if (!root || onCartPage() || !$panel()) return;
    if (!isOpen()) opener = from || document.activeElement;
    loadImages(root);
    root.removeAttribute('inert');
    root.classList.add('is-open');
    document.documentElement.classList.add('pj-cart-open');
    if (animate) fillBar(root.querySelector('[data-pj-cart-fill]'), 0);
    requestAnimationFrame(() => $panel()?.focus({ preventScroll: true }));
  };

  const close = () => {
    const root = $root();
    if (!root || !isOpen()) return;
    root.classList.remove('is-open');
    root.setAttribute('inert', '');
    document.documentElement.classList.remove('pj-cart-open');
    showError('');
    if (opener && document.contains(opener) && typeof opener.focus === 'function') opener.focus({ preventScroll: true });
    opener = null;
  };

  const postJSON = (url, body) =>
    request(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...body, sections: SECTION, sections_url: window.location.pathname }),
    });

  const subtotalText = () => $root()?.querySelector('.pj-cart__row strong')?.textContent.trim() || '';

  // one cart change at a time: a second press before the first answer would act on stale quantities
  // (e.g. "+" then "-" quickly would send quantity 0 and remove the line)
  const change = async (key, quantity) => {
    if (busy > 0 || !key || !Number.isFinite(quantity) || quantity < 0) return;
    setBusy(true);
    showError('');
    try {
      const data = await postJSON(route('changeUrl', '/cart/change.js'), { id: key, quantity });
      render(data && data.sections && data.sections[SECTION]);
      announce(quantity === 0 ? 'Item removed. ' + subtotalText() : 'Cart updated. Subtotal ' + subtotalText());
    } catch (error) {
      showError(error.message);
    } finally {
      setBusy(false);
    }
  };

  const addItems = async (items, openAfter = false) => {
    if (busy > 0) return false;
    setBusy(true);
    showError('');
    try {
      const data = await postJSON(route('addUrl', '/cart/add.js'), { items });
      render(data && data.sections && data.sections[SECTION]);
      if (openAfter) open(false);
      announce('Added to cart');
      return true;
    } catch (error) {
      if (openAfter) open(false);
      requestAnimationFrame(() => showError(error.message));
      return false;
    } finally {
      setBusy(false);
    }
  };

  // every product form on the site posts to /cart/add: send it with fetch and open the drawer instead
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || event.defaultPrevented) return;
    if (!/\/cart\/add\/?(\?.*)?$/.test(form.getAttribute('action') || '')) return;
    if (!$root() || onCartPage()) return;
    event.preventDefault();
    const button = event.submitter || form.querySelector('[type="submit"]');
    if (button && button.getAttribute('aria-busy') === 'true') return;
    button?.setAttribute('aria-busy', 'true');
    const formData = new FormData(form);
    formData.append('sections', SECTION);
    formData.append('sections_url', window.location.pathname);
    setBusy(true);
    showError('');
    try {
      const data = await request(route('addUrl', '/cart/add.js'), { method: 'POST', body: formData });
      render(data && data.sections && data.sections[SECTION]);
      open(false, button);
      announce('Added to cart');
    } catch (error) {
      // Never re-post the form natively: on a timeout or dropped connection Shopify may already have added the item.
      // Show the real cart instead, with the message (set after the drawer is open, so it is announced).
      await refresh().catch(() => {});
      open(false, button);
      requestAnimationFrame(() => showError(error.message));
    } finally {
      setBusy(false);
      button?.removeAttribute('aria-busy');
    }
  });

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-pj-cart-open]');
    if (trigger) {
      if (!$root() || onCartPage()) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
      event.preventDefault();
      open(true, trigger);
      return;
    }
    const root = $root();
    if (!root || !root.contains(event.target)) return;
    if (event.target.closest('[data-pj-cart-close]')) {
      event.preventDefault();
      close();
      return;
    }
    const qty = event.target.closest('[data-pj-cart-qty]');
    if (qty) {
      event.preventDefault();
      change(qty.getAttribute('data-key'), parseInt(qty.getAttribute('data-qty'), 10));
      return;
    }
    const gift = event.target.closest('[data-pj-cart-gift]');
    if (gift) {
      event.preventDefault();
      addItems([{ id: Number(gift.getAttribute('data-pj-cart-gift')), quantity: 1 }]);
      return;
    }
    // "You may also like": Add puts that size in the cart; the re-render then drops it from the row
    const rec = event.target.closest('[data-pj-cart-rec]');
    if (rec) {
      event.preventDefault();
      addItems([{ id: Number(rec.getAttribute('data-pj-cart-rec')), quantity: 1 }]);
      return;
    }
    const step = event.target.closest('[data-pj-cart-rec-step]');
    if (step) {
      const track = step.closest('[data-pj-cart-recs]')?.querySelector('[data-pj-cart-rec-track]');
      if (!track) return;
      const dir = Number(step.getAttribute('data-pj-cart-rec-step')) || 1;
      track.scrollBy({ left: dir * track.clientWidth * 0.8, behavior: 'smooth' });
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!isOpen()) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== 'Tab') return;
    const panel = $panel();
    if (!panel) return;
    const items = [...panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex="0"]')].filter(
      (el) => el.getClientRects().length > 0 && el.getAttribute('tabindex') !== '-1'
    );
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  // Back/Forward: bfcache restores arrive with persisted=true; Chrome's disk-cache Back arrives with persisted=false
  // but a back_forward navigation entry. Both can show an old cart.
  window.addEventListener('pageshow', (event) => {
    const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    if (event.persisted || (nav && nav.type === 'back_forward')) {
      if (onCartPage()) return;
      refresh().catch(() => {});
    }
  });

  // cart changes made by Horizon's own components (the /cart page, apps using Shopify.actions)
  document.addEventListener('shopify:cart:lines-update', (event) => {
    Promise.resolve(event.promise)
      .catch(() => null)
      .then(async (result) => {
        if (onCartPage()) {
          const cart = await request(route('cartUrl', '/cart') + '.js').catch(() => null);
          if (cart) setCounts(cart.item_count);
          return;
        }
        await refresh().catch(() => {});
        const failed = result && result.detail && result.detail.didError;
        if (event.action === 'add' && !failed) open(false);
      });
  });

  // Shopify.actions.openCart (used by Horizon and apps) opens this drawer; configured after Horizon's own override,
  // which runs at DOMContentLoaded
  window.addEventListener(
    'load',
    () => {
      const action = window.Shopify && window.Shopify.actions && window.Shopify.actions.openCart;
      if (!action || typeof action.configure !== 'function') return;
      action.configure({
        async handler() {
          if (!$root() || onCartPage()) {
            window.location.href = route('cartUrl', '/cart');
            return;
          }
          await refresh().catch(() => {});
          open(false);
        },
      });
    },
    { once: true }
  );

  // theme editor: show the drawer while its section is selected
  document.addEventListener('shopify:section:select', (event) => {
    if (event.detail && event.detail.sectionId === SECTION) open();
  });
  document.addEventListener('shopify:section:load', (event) => {
    if (event.detail && event.detail.sectionId === SECTION) open();
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    if (event.detail && event.detail.sectionId === SECTION) close();
  });

  window.pjCart = {
    open,
    close,
    refresh,
    add: (items) => addItems(items, true),
    refreshAndOpen: async () => {
      await refresh().catch(() => {});
      open(false);
    },
  };
})();
